import React from 'react';
import { Row, Col, Form, FormGroup, Label, Input, Button, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Alert, Collapse, Table } from 'reactstrap';
import apiService from 'service/apiService';
import AddressModal from '../components/AddressModal';
import { businessInformationValidations, formatFederalTaxId } from '../../../../modules/InputsValidations';
import AddressAutocomplete from '../../../AddressAutocomplete';

function BusinessInfoStep({ value, onChange, errors = {}, addresses, onAddressesChange, addressError, onClearAddressError, mode = 'APPLICATION', onDuplicateSelect, locked = false, showDuplicates = true }) {
	const [industryOptions, setIndustryOptions] = React.useState([]);
	const [businessStructureOptions, setBusinessStructureOptions] = React.useState([]);

	const [showAddressModal, setShowAddressModal] = React.useState(false);
	const [addressToEdit, setAddressToEdit] = React.useState(null);

	// Duplicates UI state (reutilizado de merchants)
	const [showDuplicatesBanner, setShowDuplicatesBanner] = React.useState(false);
	const [showDuplicatesExpanded, setShowDuplicatesExpanded] = React.useState(false);
	const [duplicateResults, setDuplicateResults] = React.useState([]);
	const [isCheckingDuplicates, setIsCheckingDuplicates] = React.useState(false);
	const debounceRef = React.useRef(null);
	const lastQueryKeyRef = React.useRef('');
	
	// Delete confirmation modal
	const [showDeleteAddressModal, setShowDeleteAddressModal] = React.useState(false);
	const [addressToDelete, setAddressToDelete] = React.useState(null);

	React.useEffect(() => {
		const load = async () => {
			try {
				const [industries, structures] = await Promise.all([
					apiService.getIndustries().catch(() => []),
					apiService.getBusinessStructures().catch(() => []),
				]);
				setIndustryOptions((industries || []).map((i) => ({ value: i.value || i.name || i, label: i.label || i.name || i })));
				setBusinessStructureOptions((structures || []).map((s) => ({ value: s.value || s.name || s, label: s.label || s.name || s })));
			} catch (e) {
				setIndustryOptions([]);
				setBusinessStructureOptions([]);
			}
		};
		load();
	}, []);

	// Chequeo de duplicados (similar a merchants BusinessInformationTab)
	React.useEffect(() => {
		if (!showDuplicates) {
			setShowDuplicatesBanner(false);
			setDuplicateResults([]);
			return;
		}
		const legal = (value.legalName || '').trim();
		const dba = (value.dbaName || '').trim();
		const tax = (value.federalTaxId || '').trim();

		const filters = [];
		if (legal) filters.push({ field: 'legal_name', operator: 'ILIKE', value: `%${legal}%` });
		if (dba) filters.push({ field: 'dba_name', operator: 'ILIKE', value: `%${dba}%` });
		if (tax) filters.push({ field: 'federal_tax_id', operator: 'ILIKE', value: `%${tax}%` });

		if (filters.length === 0) {
			setShowDuplicatesBanner(false);
			setDuplicateResults([]);
			return;
		}

		const queryKey = JSON.stringify(filters);
		if (lastQueryKeyRef.current === queryKey) return;
		lastQueryKeyRef.current = queryKey;

		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			try {
				setIsCheckingDuplicates(true);
				const merchantFilterRequestDto = apiService.convertLegacyFiltersToDto(filters);
				const data = await apiService.filterMerchants(merchantFilterRequestDto, 0, 10);
				const results = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
				setDuplicateResults(results);
				setShowDuplicatesBanner(results.length > 0);
			} catch (e) {
				setDuplicateResults([]);
			} finally {
				setIsCheckingDuplicates(false);
			}
		}, 400);

		return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
	}, [value.legalName, value.dbaName, value.federalTaxId, showDuplicates]);

	// Función para manejar el clic en el campo Federal Tax ID
	const handleFederalTaxIdClick = (e) => {
		// Mover el cursor al final del campo cuando se hace clic
		const input = e.target;
		input.setSelectionRange(input.value.length, input.value.length);
	};

	// Función para manejar las teclas en el campo Federal Tax ID
	const handleFederalTaxIdKeyDown = (e) => {
		// Permitir teclas de navegación y edición básicas
		const allowedKeys = [
			'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
			'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
			'Home', 'End'
		];
		
		// Permitir números y teclas permitidas
		if (allowedKeys.includes(e.key) || /\d/.test(e.key)) {
			return;
		}
		
		// Bloquear otras teclas
		e.preventDefault();
	};

	const handle = (e) => {
		const { name, value } = e.target;
		
		// Filter phoneNumber to only allow digits with max 25 characters
		if (name === 'phoneNumber') {
			const filtered = value.replace(/\D/g, '').slice(0, 25);
			const syntheticEvent = {
				target: { name, value: filtered }
			};
			onChange(syntheticEvent);
			return;
		}
		
		// Pass the event directly to onChange so it can handle validation and formatting
		onChange(e);
	};

	const toggleAddressModal = () => {
		setShowAddressModal(!showAddressModal);
		if (!showAddressModal) setAddressToEdit(null);
	};

	const getAddressDisplay = (address) => {
		const parts = [address.streetAddress, address.unit, address.city, address.state, address.zipCode, address.phoneNumber].filter(Boolean);
		return parts.join(', ');
	};

	const getAvailableAddressTypes = () => {
		// Permitir múltiples direcciones del mismo tipo
		const all = ['PHYSICAL', 'MAILING', 'BILLING'];
		return all;
	};

	const handleSaveAddress = (addr) => {
		if (addressToEdit) {
			onAddressesChange(addresses.map((a) => (a.id === addressToEdit.id ? { ...addr, id: a.id } : a)));
		} else {
			onAddressesChange([...(addresses || []), { ...addr, id: Date.now() }]);
		}
		setShowAddressModal(false);
		setAddressToEdit(null);
		if (addressError && onClearAddressError) onClearAddressError();
	};

	const handleDeleteAddress = (addressId) => {
		const address = addresses.find(addr => addr.id === addressId);
		setAddressToDelete(address);
		setShowDeleteAddressModal(true);
	};

	const confirmDeleteAddress = () => {
		if (addressToDelete) {
			onAddressesChange(addresses.filter(addr => addr.id !== addressToDelete.id));
			if (addressError && onClearAddressError) onClearAddressError();
		}
		setAddressToDelete(null);
		setShowDeleteAddressModal(false);
	};

	const cancelDeleteAddress = () => {
		setAddressToDelete(null);
		setShowDeleteAddressModal(false);
	};

	const alertColor = mode === 'APPLICATION' ? 'info' : 'danger';
	const alertStyle = mode === 'APPLICATION'
		? { backgroundColor: '#eef7ff', border: '1px solid #cfe2ff', color: '#084298' }
		: { backgroundColor: '#fff5f5', border: '1px solid #f8d7da', color: '#842029' };
	const alertText = mode === 'APPLICATION'
		? `Merchant Founds (${duplicateResults.length}). Select a row to fill out the form with that merchant.`
		: `Possible duplicates found (${duplicateResults.length}).`;

	return (
		<Form onSubmit={(e) => e.preventDefault()}>
			{/* Duplicates banner */}
			{showDuplicates && mode !== 'APPLICATION_INCOMPLETE' && (
			<Row>
				<Col md="12">
					<Alert
						color={alertColor}
						isOpen={showDuplicatesBanner}
						toggle={() => setShowDuplicatesBanner(false)}
						className="mb-2 py-2"
						style={alertStyle}
					>
						<i className={`ni ni-bell-55 mr-2 ${mode === 'APPLICATION' ? 'text-info' : 'text-danger'}`} />
						{alertText}
						<Button color="link" size="sm" className="ml-2 p-0" onClick={() => setShowDuplicatesExpanded(v => !v)}>
							{showDuplicatesExpanded ? 'Hide details' : 'View details'}
						</Button>
					</Alert>
					<Collapse isOpen={showDuplicatesBanner && showDuplicatesExpanded}>
						<div className="rounded p-2 mb-3" style={{ maxHeight: '220px', overflow: 'auto', backgroundColor: '#ffffff', border: alertStyle.border }}>
							<div className="table-responsive">
								<Table bordered size="sm" className="mb-0">
									<thead className="thead-light">
										<tr>
											<th>Legal Name</th>
											<th>DBA Name</th>
											<th>Federal Tax ID</th>
										</tr>
									</thead>
									<tbody>
										{duplicateResults.map((item, idx) => (
											<tr
												key={item.id || idx}
												style={mode === 'APPLICATION' ? { cursor: 'pointer' } : undefined}
												onClick={() => {
													if (mode === 'APPLICATION' && typeof onDuplicateSelect === 'function') {
														onDuplicateSelect(item);
													}
												}}
												className={mode === 'APPLICATION' ? 'wizard-dup-row' : ''}
											>
												<td>{item.legalName || item.legal_name || 'N/A'}</td>
												<td>{item.dbaName || item.dba_name || 'N/A'}</td>
												<td>{item.federalTaxId || item.federal_tax_id || 'N/A'}</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
						</div>
					</Collapse>
					{isCheckingDuplicates && (<small className="text-info d-block mt-1">Checking for duplicates...</small>)}
				</Col>
			</Row>
			)}

			{/* Business Identification Section */}
			<Row>
				<Col md="12">
					<div className="d-flex justify-content-between align-items-center">
						<h3 className="text-primary">
							<i className="ni ni-briefcase-24 mr-2"></i>
							Business Identification
						</h3>
					</div>
				</Col>
				<Col md="6" xl="3" className='mb-2'>
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="type" className="mb-1">Type: <span>*</span></Label>
						{mode === 'APPLICATION' || mode === 'APPLICATION_INCOMPLETE' ? (
							<>
								<Input
									id="type"
									name="type"
									type="text"
									value="DEALER"
									readOnly
									disabled
									className="bg-light border-secondary"
									bsSize="sm"
								/>
								<small className="form-text text-muted">
									Applications are always of type DEALER
								</small>
							</>
						) : (
							<Input
								id="type"
								name="type"
								type="select"
								value={value.type}
								onChange={handle}
								required
								className={errors.type ? 'is-invalid' : ''}
								bsSize="sm"
							>
								<option value="DEALER">DEALER</option>
								<option value="MONITORING_CENTER">MONITORING CENTER</option>
							</Input>
						)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="legalName" className="mb-1">Legal Name: <span>*</span></Label>
							<Input
								id="legalName"
								name="legalName"
								type="text"
								value={value.legalName}
								onChange={handle}
								readOnly={locked}
								disabled={locked}
								required
								maxLength={255}
								className={errors.legalName ? 'is-invalid' : ''}
								placeholder='Enter legal name'
								bsSize="sm"
							/>
							{errors.legalName && (
								<div className="invalid-feedback d-block">
									{errors.legalName}
								</div>
							)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="dbaName" className="mb-1">DBA Name: <span>*</span></Label>
						<Input
							id="dbaName"
							name="dbaName"
							type="text"
							value={value.dbaName}
							onChange={handle}
							readOnly={locked}
							disabled={locked}
							required
							maxLength={255}
							className={errors.dbaName ? 'is-invalid' : ''}
							bsSize="sm"
							placeholder='Enter DBA name'
						/>
						{errors.dbaName && (
							<div className="invalid-feedback d-block">
								{errors.dbaName}
							</div>
						)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
								<Label for="federalTaxId" className="mb-1">Federal Tax Id: <span>*</span></Label>
								<Input
									id="federalTaxId"
									name="federalTaxId"
									type="text"
									value={value.federalTaxId}
									onChange={handle}
									onClick={handleFederalTaxIdClick}
									onKeyDown={handleFederalTaxIdKeyDown}
									readOnly={locked}
									disabled={locked}
									required
									placeholder="XX-XXXXXXX"
									className={errors.federalTaxId ? 'is-invalid' : ''}
									bsSize="sm"
								/>
								{errors.federalTaxId && (
									<div className="invalid-feedback d-block">
										{errors.federalTaxId}
									</div>
								)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="industry" className="mb-1">Industry: <span>*</span></Label>
						<Input
							id="industry"
							name="industry"
							type="select"
							value={value.industry}
							onChange={handle}
							disabled={locked}
							required
							className={errors.industry ? 'is-invalid' : ''}
							bsSize="sm"
						>
							<option value="">Select Industry</option>
							{industryOptions.map(option => (
								<option key={option.value} value={option.value}>{option.label}</option>
							))}
						</Input>
						{errors.industry && (
							<div className="invalid-feedback d-block">
								{errors.industry}
							</div>
						)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="phoneNumber" className="mb-1">Phone Number: <span>*</span></Label>
						<Input
							id="phoneNumber"
							name="phoneNumber"
							type="text"
							value={value.phoneNumber}
							onChange={handle}
							readOnly={locked}
							disabled={locked}
							required
							maxLength={25}
							className={errors.phoneNumber ? 'is-invalid' : ''}
							bsSize="sm"
							placeholder="Enter phone number (ditits only)"
							/>
						{errors.phoneNumber && (
							<div className="invalid-feedback d-block">
								{errors.phoneNumber}
							</div>
						)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="website" className="mb-1">Website:</Label>
								<Input
									id="website"
									name="website"
									type="text"
									value={value.website}
									onChange={handle}
									readOnly={locked}
									disabled={locked}
									placeholder="www.example.com"
									className={errors.website ? 'is-invalid' : ''}
									bsSize="sm"
								/>
								{errors.website && (
									<div className="invalid-feedback d-block">
										{errors.website}
									</div>
								)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="dateEstablished" className="mb-1">Date Established: <span>*</span></Label>
						<Input
							id="dateEstablished"
							name="dateEstablished"
							type="date"
							value={value.dateEstablished}
							onChange={handle}
							readOnly={locked}
							disabled={locked}
							required
							max={new Date().toISOString().split('T')[0]}
							className={errors.dateEstablished ? 'is-invalid' : ''}
							bsSize="sm"
						/>
						{errors.dateEstablished && (
							<div className="invalid-feedback d-block">
								{errors.dateEstablished}
							</div>
						)}
					</FormGroup>
				</Col>
				<Col md="6" xl="6" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="stateOfInc" className="mb-1">State of Formation: <span>*</span></Label>
								<AddressAutocomplete
									value={value.stateOfInc}
									onChange={(text) => {
										// Al escribir, solo actualiza el texto y limpia dependencias si fuese necesario
										const syntheticEvent = { target: { name: 'stateOfInc', value: (text || '').slice(0, 255) } };
										onChange(syntheticEvent);
									}}
									onSelect={(item) => {
										// Al seleccionar, colocar solo el estado retornado por la API
										const details = item?.details || {};
										const onlyState = details.state || '';
										const syntheticEvent = { target: { name: 'stateOfInc', value: onlyState } };
										onChange(syntheticEvent);
									}}
									placeholder="Enter state to search"
									disabled={locked}
									className={errors.stateOfInc ? 'is-invalid' : ''}
									maxLength={255}
									bsSize="sm"
								/>
								{errors.stateOfInc && (
									<div className="invalid-feedback d-block">
										{errors.stateOfInc}
									</div>
								)}
					</FormGroup>
				</Col>
				<Col md="6" xl="3" className="mb-2">
					<FormGroup className='p-0 m-0 mb-2'>
						<Label for="businessStructure" className="mb-1">Business Structure: <span>*</span></Label>
						<Input
							id="businessStructure"
							name="businessStructure"
							type="select"
							value={value.businessStructure}
							onChange={handle}
							disabled={locked}
							required
							className={errors.businessStructure ? 'is-invalid' : ''}
							bsSize="sm"
						>
							<option value="">Select Business Structure</option>
							{businessStructureOptions.map(option => (
								<option key={option.value} value={option.value}>{option.label}</option>
							))}
						</Input>
						{errors.businessStructure && (
							<div className="invalid-feedback d-block">
								{errors.businessStructure}
							</div>
						)}
					</FormGroup>
				</Col>
			</Row>

			<hr></hr>

			{/* Address Information Section */}
			<Row className="mt-2">
				<Col md="12">
					<h3 className="text-primary">
						<i className="ni ni-pin-3 mr-2"></i>
						Business Addresses
					</h3>
				</Col>
			</Row>
			<div>
				{addresses.length === 0 ? (
					<div>
						<p className='m-0'>Press the button below to add an address (at least one is required).</p>
						{addressError && (<div className="text-danger small mt-1">{addressError}</div>)}
					</div>
				) : (
					<ul className="list-group my-2">
						{
							addresses.map((address, index) => (
								<li key={index} className="list-group-item d-flex justify-content-between align-items-center">
									<div className="flex-grow-1">
										<h4 className='text-primary'>{address.addressType} <span className='text-dark mx-1'>•</span> {address.phoneNumber}</h4>
										<p className='fw-bolder text-dark mb-0'>{address.streetAddress}</p>
										<p className='mb-0'>
											{[address.country, address.city, address.state, address.zipCode]
												.filter(field => field && field.trim() !== '')
												.join(', ')}
										</p>
									</div>

									<div className="btn-group">
										<Button 
											color="primary"
											size='md'
											title='Edit address'
											onClick={() => { setAddressToEdit(address); setShowAddressModal(true); }}>
											<i className='ni ni-settings-gear-65'></i>
										</Button>
										<Button 
											color="secondary" 
											size='md'
											title='Remove address'
											onClick={() => handleDeleteAddress(address)}>
												<i className='ni ni-fat-remove'></i>
										</Button>
									</div>
								</li>
							))
						}
					</ul>
				)}

				<Button
					className='mt-2'
					color="outline-primary" 
					size="sm"
					onClick={toggleAddressModal}
					disabled={locked}>
					<i className="ni ni-fat-add mr-1"></i>
					Add address
				</Button>
			</div>

			<hr></hr>

			<AddressModal
				isOpen={showAddressModal}
				toggle={toggleAddressModal}
				onSave={handleSaveAddress}
				addressToEdit={addressToEdit}
				getAvailableAddressTypes={getAvailableAddressTypes}
			/>

			{/* Delete Address Modal */}
			<Modal isOpen={showDeleteAddressModal} toggle={cancelDeleteAddress} size="sm" centered>
				<ModalHeader toggle={cancelDeleteAddress}>
					<i className="ni ni-fat-remove mr-2 text-danger" />
					Confirm Address Deletion
				</ModalHeader>
				<ModalBody>
					<p className="mb-0">
						Are you sure you want to delete this address?
					</p>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={cancelDeleteAddress}>
						<i className="ni ni-fat-remove mr-2" />
						Cancel
					</Button>
					<Button color="danger" onClick={confirmDeleteAddress}>
						<i className="ni ni-fat-remove mr-2" />
						Delete Address
					</Button>
				</ModalFooter>
			</Modal>
		</Form>
	);
}

export default BusinessInfoStep;




